/**
 * usePreviewSizing
 * ---
 * Sizes the bordered preview frame so the on-screen preview matches the
 * canvas's actual aspect ratio (1200 × clamp(content, 1200, 1800)) instead
 * of being forced into a square. Returns refs to attach plus the measured
 * frame and inner-canvas dimensions.
 *
 * Floor 1200 / cap 1800 are enforced upstream (in ExcerptImagePreview's
 * minHeight/maxHeight + clamp here) so a short quote stays 1:1 and a
 * very long quote can't blow out the layout.
 */
import { useEffect, useRef, useState } from "react";

export const CANVAS_WIDTH = 1200;
export const CANVAS_MIN_HEIGHT = 1200;
export const CANVAS_MAX_HEIGHT = 1800;

export const usePreviewSizing = (canvasNaturalHeight: number) => {
  const previewColumnRef = useRef<HTMLDivElement>(null);
  const previewBoxRef = useRef<HTMLDivElement>(null);
  const [colSize, setColSize] = useState({ w: 600, h: 600 });
  const [previewWidth, setPreviewWidth] = useState(600);
  const [viewportH, setViewportH] = useState(
    typeof window === "undefined" ? 800 : window.innerHeight,
  );

  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Track the available column space (minus the "Preview · 1200 × auto" label).
  useEffect(() => {
    const col = previewColumnRef.current;
    if (!col) return;
    const update = () => {
      const w = col.clientWidth;
      const label = col.querySelector<HTMLElement>("[data-preview-label]");
      const labelH = label ? label.offsetHeight + 8 : 0;
      const h = Math.max(0, col.clientHeight - labelH);
      setColSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(col);
    return () => ro.disconnect();
  }, []);

  // Inner canvas width = frame width minus the wrapper's padding (p-2 / sm:p-3).
  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return;
    const update = () => setPreviewWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Clamp natural height to [1200, 1800]; aspect = height / width.
  const clampedCanvasHeight = Math.min(
    CANVAS_MAX_HEIGHT,
    Math.max(CANVAS_MIN_HEIGHT, canvasNaturalHeight || CANVAS_MIN_HEIGHT),
  );
  const aspect = clampedCanvasHeight / CANVAS_WIDTH;

  // Cap visible frame at 600px wide. Also shrink so the whole frame
  // (including label + overflow warning) fits above the fold of the
  // app scroll container — we approximate that with viewport height
  // minus a chrome budget. Width derived from height to preserve aspect.
  const PREVIEW_MAX_WIDTH = 600;
  const VIEWPORT_CHROME = 220; // header + tabs + label + padding budget
  const availableHeight = Math.max(280, viewportH - VIEWPORT_CHROME);
  const colWidth = colSize.w > 0 ? colSize.w : PREVIEW_MAX_WIDTH;
  const widthByCol = Math.min(colWidth, PREVIEW_MAX_WIDTH);
  const widthByHeight = availableHeight / aspect;
  const frameWidth = Math.max(220, Math.min(widthByCol, widthByHeight));
  const frameHeight = frameWidth * aspect;
  const previewHeight = previewWidth * aspect;

  return {
    previewColumnRef,
    previewBoxRef,
    frameWidth,
    frameHeight,
    previewWidth,
    previewHeight,
    clampedCanvasHeight,
  };
};
